import React, { useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import {
  BrainIcon,
  BriefcaseBusinessIcon,
  CheckIcon,
  CircleCheckIcon,
  ClipboardCheckIcon,
  HeartHandshakeIcon,
  SparklesIcon,
  UsersRoundIcon,
  XIcon,
} from '../../../icons/animated';
import { ADMIN_PAGES_KEYS, NS } from '../../../i18n/constants/keys';
import { useCreateJobOpeningMutation } from '../../../utils/api_service/jobOpeningsApi';
import { JobOpeningsContext } from '../../../utils/contexts/JobOpening';
import { RootState } from '../../../utils/redux_store/store';
import { SubscriptionDetails } from '../../../utils/types/admin_types';
import { toast } from '../../../ui/toast';
import { JOB_OPENING_TYPE } from '../Constants';

export interface ICreateJobOpeningDialog {
  open: boolean;
  onClose: () => void;
  onSuccessClose?: () => void;
}

interface ElementDefinition {
  title: JOB_OPENING_TYPE;
  label: string;
  icon: React.ReactNode;
  helpText: string;
}

export const CreateJobOpeningDialog = ({
  open,
  onClose,
  onSuccessClose,
}: ICreateJobOpeningDialog) => {
  const { t } = useTranslation(NS.adminPages);
  const history = useHistory();
  const { setJobOpeningElements, setCompletedJobOpeningDetails } =
    useContext(JobOpeningsContext);

  const { subscriptions } = useSelector(
    (state: RootState) => state.subscriptions,
  );

  const [createJobOpeningMutation, { isLoading: loading }] =
    useCreateJobOpeningMutation();

  const helpTextMap: Record<JOB_OPENING_TYPE, string> = {
    [JOB_OPENING_TYPE.JOB_POSTING]: t(
      ADMIN_PAGES_KEYS.jobOpenings.createDialog.helpText.jobPosting,
    ),
    [JOB_OPENING_TYPE.APPLICANT_ANALYSIS]: t(
      ADMIN_PAGES_KEYS.jobOpenings.createDialog.helpText.applicantAnalysis,
    ),
    [JOB_OPENING_TYPE.AUTO_SUITABILITY]: t(
      ADMIN_PAGES_KEYS.jobOpenings.createDialog.helpText.autoSuitability,
    ),
    [JOB_OPENING_TYPE.AUTO_OUTSTANDING]: t(
      ADMIN_PAGES_KEYS.jobOpenings.createDialog.helpText.autoOutstanding,
    ),
    [JOB_OPENING_TYPE.AUTO_ASSESMENTS]: t(
      ADMIN_PAGES_KEYS.jobOpenings.createDialog.helpText.autoAssessments,
    ),
    [JOB_OPENING_TYPE.SMART_INTERVIEWS]: t(
      ADMIN_PAGES_KEYS.jobOpenings.createDialog.helpText.smartInterviews,
    ),
    [JOB_OPENING_TYPE.CCA]: '',
    [JOB_OPENING_TYPE.JOB_OPENING_DETAILS]: '',
    [JOB_OPENING_TYPE.ANALITICS]: '',
    [JOB_OPENING_TYPE.PSYCHOMETRIC]: t(
      ADMIN_PAGES_KEYS.jobOpenings.createDialog.helpText.psychometric,
    ),
    [JOB_OPENING_TYPE.WELLNESS]: t(
      ADMIN_PAGES_KEYS.jobOpenings.createDialog.helpText.wellness,
    ),
  };

  const allElements: ElementDefinition[] = [
    {
      title: JOB_OPENING_TYPE.JOB_POSTING,
      label: 'JOB POSTING',
      icon: <BriefcaseBusinessIcon size={26} />,
      helpText: helpTextMap[JOB_OPENING_TYPE.JOB_POSTING],
    },
    {
      title: JOB_OPENING_TYPE.APPLICANT_ANALYSIS,
      label: 'APPLICANT ANALYSIS',
      icon: <UsersRoundIcon size={26} />,
      helpText: helpTextMap[JOB_OPENING_TYPE.APPLICANT_ANALYSIS],
    },
    {
      title: JOB_OPENING_TYPE.AUTO_SUITABILITY,
      label: 'AUTO-SUITABILITY',
      icon: <CircleCheckIcon size={26} />,
      helpText: helpTextMap[JOB_OPENING_TYPE.AUTO_SUITABILITY],
    },
    {
      title: JOB_OPENING_TYPE.AUTO_OUTSTANDING,
      label: 'AUTO-OUTSTANDING',
      icon: <SparklesIcon size={26} />,
      helpText: helpTextMap[JOB_OPENING_TYPE.AUTO_OUTSTANDING],
    },
    {
      title: JOB_OPENING_TYPE.AUTO_ASSESMENTS,
      label: 'AUTO-ASSESSMENTS',
      icon: <ClipboardCheckIcon size={26} />,
      helpText: helpTextMap[JOB_OPENING_TYPE.AUTO_ASSESMENTS],
    },
    {
      title: JOB_OPENING_TYPE.SMART_INTERVIEWS,
      label: 'SMART INTERVIEWS',
      icon: <BrainIcon size={26} />,
      helpText: helpTextMap[JOB_OPENING_TYPE.SMART_INTERVIEWS],
    },
    {
      title: JOB_OPENING_TYPE.PSYCHOMETRIC,
      label: 'PSYCHOMETRIC',
      icon: <BrainIcon size={26} />,
      helpText: helpTextMap[JOB_OPENING_TYPE.PSYCHOMETRIC],
    },
    {
      title: JOB_OPENING_TYPE.WELLNESS,
      label: 'WELLNESS',
      icon: <HeartHandshakeIcon size={26} />,
      helpText: helpTextMap[JOB_OPENING_TYPE.WELLNESS],
    },
  ];

  const isLicenseActive = (feature?: {
    startDate: number | string;
    endDate: number | string;
  }): boolean => {
    if (!feature) return false;
    const now = Date.now();
    const end = Number(feature.endDate) * 1000;
    return end > now;
  };

  const getEnabledJobOpeningTypes = (
    subs: SubscriptionDetails | undefined,
  ): Set<JOB_OPENING_TYPE> => {
    const enabled = new Set<JOB_OPENING_TYPE>();
    if (!subs) return enabled;

    if (isLicenseActive(subs.hierTalentBase))
      enabled.add(JOB_OPENING_TYPE.APPLICANT_ANALYSIS);
    if (isLicenseActive(subs.hierTalentAutoClassify))
      enabled.add(JOB_OPENING_TYPE.AUTO_SUITABILITY);
    if (isLicenseActive(subs.hierTalentAutoClassify))
      enabled.add(JOB_OPENING_TYPE.AUTO_OUTSTANDING);
    if (isLicenseActive(subs.hierTalentJobPost))
      enabled.add(JOB_OPENING_TYPE.JOB_POSTING);
    if (isLicenseActive(subs.hierTalentAutoAssessments))
      enabled.add(JOB_OPENING_TYPE.AUTO_ASSESMENTS);
    if (isLicenseActive(subs.hierTalentSmartInterviews))
      enabled.add(JOB_OPENING_TYPE.SMART_INTERVIEWS);
    if (isLicenseActive(subs.hierTalentPsychometricFaxtor))
      enabled.add(JOB_OPENING_TYPE.PSYCHOMETRIC);
    if (isLicenseActive(subs.hierTalentWellness))
      enabled.add(JOB_OPENING_TYPE.WELLNESS);

    return enabled;
  };

  const enabledTypes = getEnabledJobOpeningTypes(subscriptions);
  const visibleElements = allElements.filter(({ title }) =>
    enabledTypes.has(title),
  );

  const alwaysHidden = [
    JOB_OPENING_TYPE.JOB_OPENING_DETAILS,
    JOB_OPENING_TYPE.ANALITICS,
    JOB_OPENING_TYPE.CCA,
  ];

  const disabled = [
    JOB_OPENING_TYPE.JOB_OPENING_DETAILS,
    JOB_OPENING_TYPE.APPLICANT_ANALYSIS,
  ];
  const defaultSelected = [
    JOB_OPENING_TYPE.JOB_OPENING_DETAILS,
    JOB_OPENING_TYPE.APPLICANT_ANALYSIS,
  ];

  const [selectedTypes, setSelectedTypes] = useState<JOB_OPENING_TYPE[]>([
    ...defaultSelected,
  ]);

  const selectableElements = visibleElements.filter(
    (item) => !disabled.includes(item.title),
  );
  const allSelected =
    selectableElements.length > 0 &&
    selectableElements.every((item) => selectedTypes.includes(item.title));

  const handleToggle = (type: JOB_OPENING_TYPE) => {
    if (disabled.includes(type)) return;
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  };

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedTypes([...defaultSelected]);
    } else {
      const allSelectable = selectableElements.map((item) => item.title);
      setSelectedTypes([...disabled, ...allSelectable]);
    }
  };

  const handleCreate = async () => {
    try {
      const result = await createJobOpeningMutation(selectedTypes).unwrap();
      history.push(`/job-openings/${result.id}`);
      setJobOpeningElements([]);
      setCompletedJobOpeningDetails([]);
      if (onSuccessClose) onSuccessClose();
      else onClose();
    } catch (error) {
      toast.error('Error Creating Job Opening', {
        position: 'bottom-center',
      });
    }
  };

  const visibleSelectedCount = selectedTypes.filter(
    (tp) => !alwaysHidden.includes(tp),
  ).length;
  const visibleTotalCount = visibleElements.filter(
    (i) => !alwaysHidden.includes(i.title),
  ).length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(4px)',
          bgcolor: 'rgba(15, 23, 42, 0.35)',
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 4,
          p: { xs: 2.5, sm: 3.5 },
          bgcolor: '#ffffff',
          boxShadow: '0 25px 60px -15px rgba(15, 23, 42, 0.16), 0 0 1px 1px rgba(15, 23, 42, 0.05)',
          border: '1px solid rgba(226, 232, 240, 0.9)',
          position: 'relative',
          overflow: 'hidden',
        },
      }}
    >
      {/* Subtle light ambient glow */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          transform: 'translate(35%, -35%)',
          width: 260,
          height: 260,
          borderRadius: '50%',
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.15),
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          transform: 'translate(-35%, 35%)',
          width: 260,
          height: 260,
          borderRadius: '50%',
          bgcolor: (theme) => alpha(theme.palette.primary.light || theme.palette.primary.main, 0.12),
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }}
      />

      {/* Header: Title & Close Button */}
      <DialogTitle sx={{ p: 0, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9' }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              boxShadow: (theme) => `0 0 8px ${alpha(theme.palette.primary.main, 0.6)}`,
            }}
          />
          <Typography
            sx={{
              fontWeight: 700,
              fontFamily: 'monospace',
              letterSpacing: '0.22em',
              fontSize: '0.75rem',
              color: 'primary.dark',
              textTransform: 'uppercase',
            }}
          >
            CREATE NEW
          </Typography>
        </Stack>

        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close dialog"
          sx={{
            bgcolor: '#f1f5f9',
            color: '#64748b',
            borderRadius: 2.5,
            border: '1px solid #e2e8f0',
            p: 0.75,
            transition: 'all 0.15s ease',
            '&:hover': {
              bgcolor: '#e2e8f0',
              color: '#0f172a',
            },
          }}
        >
          <XIcon size={16} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, pt: 2, overflow: 'visible' }}>
        {/* Sub-bar: Selection stats & Select All */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, px: 0.5 }}>
          <ButtonBase
            onClick={handleSelectAll}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              cursor: 'pointer',
              borderRadius: 1,
              '&:hover .select-all-label': { color: 'primary.main' },
            }}
          >
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: '4px',
                border: '1px solid',
                borderColor: allSelected ? 'primary.main' : '#cbd5e1',
                bgcolor: allSelected ? 'primary.main' : '#ffffff',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.15s ease',
              }}
            >
              {allSelected && <CheckIcon size={12} />}
            </Box>
            <Typography
              className="select-all-label"
              sx={{
                fontSize: '0.78rem',
                fontWeight: 600,
                fontFamily: 'monospace',
                color: '#334155',
                letterSpacing: '0.02em',
                transition: 'color 0.15s',
              }}
            >
              {t(ADMIN_PAGES_KEYS.jobOpenings.createDialog.selectAll)}
            </Typography>
          </ButtonBase>

          <Chip
            label={`${visibleSelectedCount} / ${visibleTotalCount} SELECTED`}
            size="small"
            sx={{
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              color: 'primary.dark',
              border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              fontWeight: 700,
              fontFamily: 'monospace',
              fontSize: '0.7rem',
              letterSpacing: '0.06em',
              height: 24,
              borderRadius: 9999,
            }}
          />
        </Box>

        {/* Grid Matrix (2x4) */}
        <Grid
          container
          sx={{
            border: '1px solid #e2e8f0',
            borderRadius: 3.5,
            overflow: 'hidden',
            bgcolor: 'rgba(248, 250, 252, 0.5)',
            my: 1.5,
          }}
        >
          {visibleElements.map((item, idx) => {
            const isSelected = selectedTypes.includes(item.title);
            const isDisabled = disabled.includes(item.title);

            // Matrix border dividers calculation
            const isRightCol = (idx + 1) % 4 === 0;
            const isRightColSm = (idx + 1) % 2 === 0;
            const isBottomRow = idx >= visibleElements.length - 2;
            const isTopRowDesktop = idx < 4 && visibleElements.length > 4;

            return (
              <Grid
                item
                xs={6}
                sm={3}
                key={item.title}
                sx={{
                  borderRight: {
                    xs: isRightColSm ? 'none' : '1px solid #e2e8f0',
                    sm: isRightCol ? 'none' : '1px solid #e2e8f0',
                  },
                  borderBottom: {
                    xs: isBottomRow ? 'none' : '1px solid #e2e8f0',
                    sm: isTopRowDesktop ? '1px solid #e2e8f0' : 'none',
                  },
                }}
              >
                <Tooltip title={item.helpText} arrow placement="top">
                  <ButtonBase
                    onClick={() => handleToggle(item.title)}
                    disabled={isDisabled}
                    sx={{
                      width: '100%',
                      height: '100%',
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center',
                      gap: 1.25,
                      p: { xs: 2.25, sm: 2.75 },
                      bgcolor: (theme) => (isSelected ? alpha(theme.palette.primary.main, 0.06) : '#ffffff'),
                      color: isSelected ? 'primary.main' : '#64748b',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: isDisabled ? 'default' : 'pointer',
                      '&:hover': !isDisabled
                        ? {
                            bgcolor: (theme) => (isSelected ? alpha(theme.palette.primary.main, 0.12) : '#f8fafc'),
                            color: isSelected ? 'primary.dark' : '#0f172a',
                          }
                        : undefined,
                    }}
                  >
                    {/* Active Checkmark Badge in Corner */}
                    {isSelected && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 10,
                          right: 10,
                          width: 17,
                          height: 17,
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: (theme) => `0 2px 6px ${alpha(theme.palette.primary.main, 0.35)}`,
                        }}
                      >
                        <CheckIcon size={11} />
                      </Box>
                    )}

                    {/* Icon */}
                    <Box
                      sx={{
                        color: isSelected ? 'primary.main' : '#64748b',
                        filter: isSelected
                          ? (theme) => `drop-shadow(0 2px 8px ${alpha(theme.palette.primary.main, 0.35)})`
                          : 'none',
                        transform: isSelected ? 'scale(1.08)' : 'scale(1)',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {item.icon}
                    </Box>

                    {/* Uppercase Matrix Label */}
                    <Typography
                      sx={{
                        fontSize: '0.66rem',
                        fontFamily: 'monospace',
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        lineHeight: 1.25,
                        color: isSelected ? 'primary.main' : '#475569',
                      }}
                    >
                      {item.label}
                    </Typography>
                  </ButtonBase>
                </Tooltip>
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>

      {/* Bottom Actions: Cancel & Create Button */}
      <DialogActions sx={{ p: 0, pt: 2.5, mt: 1, borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Button
          onClick={onClose}
          sx={{
            px: 3,
            py: 1.1,
            borderRadius: 2.5,
            bgcolor: '#f1f5f9',
            border: '1px solid #e2e8f0',
            color: '#475569',
            fontWeight: 600,
            fontSize: '0.78rem',
            fontFamily: 'monospace',
            textTransform: 'none',
            letterSpacing: '0.03em',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
            transition: 'all 0.15s ease',
            '&:hover': {
              bgcolor: '#e2e8f0',
              color: '#0f172a',
            },
          }}
        >
          {t(ADMIN_PAGES_KEYS.jobOpenings.createDialog.cancel)}
        </Button>

        <Button
          onClick={handleCreate}
          disabled={
            loading ||
            selectedTypes.filter((tp) => !alwaysHidden.includes(tp)).length === 0
          }
          sx={{
            px: 3.5,
            py: 1.1,
            borderRadius: 2.5,
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            color: '#ffffff',
            fontWeight: 800,
            fontSize: '0.78rem',
            fontFamily: 'monospace',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            boxShadow: (theme) => `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
            transition: 'all 0.2s ease',
            '&:hover': {
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
              boxShadow: (theme) => `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
            },
            '&:disabled': {
              opacity: 0.45,
              color: '#ffffff',
              boxShadow: 'none',
            },
          }}
        >
          {loading && (
            <CircularProgress size={14} sx={{ color: '#ffffff', mr: 1 }} />
          )}
          {loading
            ? t(ADMIN_PAGES_KEYS.jobOpenings.createDialog.creating)
            : t(ADMIN_PAGES_KEYS.jobOpenings.createDialog.createButton)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
